export const useUserApi = () => {
  const userStore = useUserStore();
  const { ensureAuthenticated, setCurrencyCookies } = useDjustAuth();

  /**
   * Create customer user
   * @param params - The parameters of the user to create
   */
  const createCustomerUser = async (
    params: CreateCustomerUserRequest
  ): Promise<CreateCustomerUserResponse | null> => {
    try {
      await ensureAuthenticated();

      // Make an API call using useFetch
      const { data, error } = await useFetch<CreateCustomerUserResponse>('/api/users/create', {
        method: 'POST',
        body: params,
        key: `createuser-${JSON.stringify(params)}`, // Unique key for caching
      });

      // Check for errors
      if (error.value) {
        console.error('Failed to create user:', error.value);
        throw new Error('Failed to create a new user.');
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while creating user:', error);
      throw new Error('Failed to create user. Please try again later.');
    }
  };

  /**
   * Update customer user
   * @param params - The parameters to update
   */
  const updateCustomerUser = async (
    params: UpdateCustomerUserRequest
  ): Promise<UpdateCustomerUserResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to update the user
      const { data, error } = await useFetch<UpdateCustomerUserResponse>(`/api/users/update`, {
        method: 'PUT',
        body: params,
        key: `updateuser-${JSON.stringify(params)}`, // Unique key for caching
      });

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to update user:', error.value);
        switch (error.value.statusCode) {
          case 404:
            throw new Error('User not found.');
          default:
            throw new Error('Failed to update user.');
        }
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to update user:', error.message || error);
      throw new Error('An unexpected error occurred while updating the user.');
    }
  };

  /**
   * Fetch user addresses (shipping, billing, etc.)
   * @param params - The parameters to filter user addresses
   */
  const getUserAddresses = async (
    params: GetCustomerUserAddressesRequest = {}
  ): Promise<GetCustomerUserAddressesResponse | null> => {
    try {
      await ensureAuthenticated();

      // Make an API call using useFetch
      const { data, error } = await useFetch<GetCustomerUserAddressesResponse>(
        '/api/users/addresses',
        {
          params,
          key: `getuseraddresses-${JSON.stringify(params)}`, // Unique key for caching
        }
      );

      // Check for errors
      if (error.value) {
        console.error("Failed to retrieve user's addresses:", error.value);
        throw new Error("Failed to retrieve user's addresses:.");
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error("Unexpected error while retrieving user's addresses:", error.message || error);
      throw new Error("Failed to retrieve user's addresses. Please try again later.");
    }
  };

  /**
   * Fetch user shipping addresses
   * @param useStore - Whether to store the results in the reactive state
   */
  const getUserShippingAddresses = async (
    useStore = true
  ): Promise<GetCustomerUserAddressesResponse> => {
    try {
      const response = await getUserAddresses({ shipping: true });

      if (response) {
        if (useStore) {
          userStore.shippingAddresses = response.addresses || [];
        }
        return response;
      } else {
        console.error('No shipping address data found');
        return { success: false };
      }
    } catch (error) {
      console.error('Failed to fetch shipping addresses:', error);
      return { success: false };
    }
  };

  /**
   * Fetch user billing addresses
   * @param useStore - Whether to store the results in the reactive state
   */
  const getUserBillingAddresses = async (
    useStore = true
  ): Promise<GetCustomerUserAddressesResponse> => {
    try {
      const response = await getUserAddresses({ billing: true });

      if (response) {
        if (useStore) {
          userStore.billingAddresses = response.addresses || [];
        }
        return response;
      } else {
        console.error('No billing address data found');
        return { success: false };
      }
    } catch (error) {
      console.error('Failed to fetch billing addresses:', error);
      return { success: false };
    }
  };

  /**
   * Fetch user organisations (shipping, billing, etc.)
   * @param params - The parameters to filter user organisations
   */
  const getUserOrganisations = async (): Promise<GetCustomerUserOrganisationsResponse | null> => {
    try {
      await ensureAuthenticated();

      // Make an API call using useFetch
      const { data, error } = await useFetch<GetCustomerUserOrganisationsResponse>(
        '/api/users/organisations',
        {
          key: `getuserorganisations`, // Unique key for caching
        }
      );

      // Check for errors
      if (error.value) {
        console.error("Failed to retrieve user's organisations:", error.value);
        throw new Error("Failed to retrieve user's organisations:.");
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error(
        "Unexpected error while retrieving user's organisations:",
        error.message || error
      );
      throw new Error("Failed to retrieve user's organisations. Please try again later.");
    }
  };

  /**
   * Activate customer user
   * @param params - The parameters to activate the user
   */
  const activateCustomerUser = async (
    params: ActivateCustomerUserRequest
  ): Promise<ActivateCustomerUserResponse | null> => {
    try {
      const response = await $fetch<ActivateCustomerUserResponse>('/api/users/activate', {
        method: 'POST',
        body: params,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response;
    } catch (e) {
      const error = e as ApiError;
      console.error('useUserApi - activateCustomerUser - Error:', error);
      throw error;
    }
  };

  /**
   * Send customer user activation request email
   * @param params - The parameters to send the user activation request
   */
  const sendCustomerUserActivationRequest = async (
    params: SendCustomerUserActivationRequestRequest
  ): Promise<ResendActivationRequestCustomerUserResponse | null> => {
    try {
      // Make an API call using useFetch
      const { data, error } = await useFetch<ResendActivationRequestCustomerUserResponse>(
        '/api/users/resend-activation-request',
        {
          method: 'POST',
          body: params,
          key: `sendactivationrequesteuser-${JSON.stringify(params)}`, // Unique key for caching
        }
      );

      // Check for errors
      if (error.value) {
        console.error('Failed to send customer user activation request:', error.value);
        throw new Error('Failed to send customer user activation request.');
      }

      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while sending customer user activation request:', error);
      throw new Error('Failed to send customer user activation request. Please try again later.');
    }
  };

  /**
   * Switch customer account with optional parameters like storeId and storeViewId.
   */
  const switchUserCurrency = (currency: string): void => {
    // Set cookies account ID in cookies (Ensure this function is correctly implemented)
    currency ? setCurrencyCookies(currency) : null;
    // Update user store if the response contains account data
    const userStore = useUserStore(); // Ensure this store is imported properly
    userStore.currency = currency;
  };

  return {
    createCustomerUser,
    updateCustomerUser,
    getUserAddresses,
    getUserShippingAddresses,
    getUserBillingAddresses,
    getUserOrganisations,
    activateCustomerUser,
    sendCustomerUserActivationRequest,
    switchUserCurrency,
  };
};
